import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaDownload, FaTimes, FaCheckCircle, FaTimesCircle, FaRedo } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Bulkprocessing.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const CERT_WEBHOOK = 'https://sms.definiteassurance.com/webhook/c924c7c4-d115-4724-a78c-27048b99b6f0';
const PAYMENT_WEBHOOK = 'https://workflows.uuidesign.xyz/webhook/0623c8f7-d286-45c4-b823-2675d25de35a';
const COMPANY = 'Definite';
const STAMP_DUTY = 40;

const EXPECTED_CSV_COLS = [
  'CustomerFirstName','CustomerLastName','CustomerIDNumber','CustomerPhoneNumber',
  'CustomerKRA','CommencementDate','Registration','SumInsured','Capacity',
  'Make','Model','ChassisNumber','EngineNumber','YOM','CC','Color'
];

const DISPLAY_COLS = [
  'CustomerFirstName','CustomerLastName','CustomerIDNumber','CustomerPhoneNumber',
  'CustomerKRA','CommencementDate','CommencementUnix','ExpiryUnix','Registration',
  'Capacity','Make','MakeCode','Model','ModelCode','ChassisNumber','EngineNumber',
  'YOM','CC','Color','GrossPremium'
];

const STEPS = [
  { key: 'config', label: 'Configure' },
  { key: 'upload', label: 'Upload & Process' },
  { key: 'payment', label: 'Payment & Certificates' },
];

// ══════════════════════════════════════
//  HELPERS (extracted & deduplicated)
// ══════════════════════════════════════
const pad = (n, z = 2) => String(n).padStart(z, '0');

const formatDateTime = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(),3)}`;
};

const getNow = () => formatDateTime(new Date());

const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

const formatDateShort = (dateStr) => {
  try {
    const d = new Date(dateStr);
    return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
  } catch { return '--'; }
};

/** Parse dd/MM/yyyy or dd-MM-yyyy → Date at 12:22:00 */
const parseDdMmYyyy = (str) => {
  if (!str) return null;
  const parts = String(str).trim().split(/[\/\-]/);
  if (parts.length < 3) return null;
  const [day, month, year] = parts.map(Number);
  if ([day, month, year].some(isNaN)) return null;
  return new Date(year, month - 1, day, 12, 22, 0, 0);
};

const addDays = (date, days) => {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
};

const addMonths = (date, months) => {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
};

const lastDayOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  return d;
};

/** Single function to compute expiry from commencement + coverPeriod — replaces 3 duplicate blocks */
const computeExpiry = (commDate, cp) => {
  const strategies = {
    'Week':            () => addDays(commDate, 6),
    'Fortnight':       () => addDays(commDate, 13),
    'Monthly':         () => addDays(lastDayOfMonth(commDate), -1),
    '3Installments':   () => addDays(lastDayOfMonth(addMonths(commDate, 3)), -1),
    '3installments':   () => addDays(lastDayOfMonth(addMonths(commDate, 3)), -1),
    '6Installments':   () => addDays(lastDayOfMonth(addMonths(commDate, 6)), -1),
    '6installments':   () => addDays(lastDayOfMonth(addMonths(commDate, 6)), -1),
    '10Installments':  () => addDays(lastDayOfMonth(addMonths(commDate, 10)), -1),
    '10installments':  () => addDays(lastDayOfMonth(addMonths(commDate, 10)), -1),
    'Annually':        () => addDays(commDate, 364),
    'Annual':          () => addDays(commDate, 364),
    '2installments':   () => addDays(lastDayOfMonth(addMonths(commDate, 6)), -1),
  };
  return (strategies[cp] || (() => addDays(lastDayOfMonth(commDate), -1)))();
};

/** Decompose GrossPremium → base, levies, commission components */
const decomposePremium = (gross) => {
  const g = parseFloat(gross) || 0;
  const base = g > STAMP_DUTY ? (g - STAMP_DUTY) / 1.0045 : 0;
  const tl = Math.round(0.002 * base);
  const phcf = Math.round(0.0025 * base);
  const basicPremium = Math.round(base + tl + phcf + STAMP_DUTY);
  const commission = Math.round(0.1 * base);
  const netComDeduction = Math.round(0.1 * commission);
  const netCommission = commission - netComDeduction;
  const netPremium = basicPremium - netCommission;
  return { base: Math.round(base), tl, phcf, basicPremium, commission, netCommission, netPremium, stampduty: STAMP_DUTY };
};

/** Cover days (inclusive) between two date strings */
const computeCoverDays = (commStr, expStr) => {
  try {
    const diff = new Date(expStr).getTime() - new Date(commStr).getTime();
    return Math.ceil(diff / 86400000) + 1;
  } catch { return 0; }
};

/** CSV parser with proper quote handling */
const parseCsvText = (text) => {
  if (!text) return [];
  const clean = text.replace(/\r/g, '').trim();
  if (!clean) return [];
  const lines = clean.split('\n').filter(l => l.trim());
  if (!lines.length) return [];

  const splitLine = (line) => {
    const result = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === ',' && !inQ) { result.push(cur); cur = ''; }
      else cur += ch;
    }
    result.push(cur);
    return result.map(s => s.trim().replace(/^"|"$/g, ''));
  };

  const headers = splitLine(lines[0]).map(h => h.replace(/^\uFEFF/, '').trim());
  const rows = lines.slice(1).map(line => {
    const cols = splitLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cols[i] ?? ''; });
    return obj;
  });

  // Normalise to expected columns (case-insensitive)
  const mapped = rows.map(r => {
    const out = {};
    EXPECTED_CSV_COLS.forEach(h => {
      const key = Object.keys(r).find(k => k.toLowerCase() === h.toLowerCase());
      out[h] = key ? r[key] : '';
    });
    return out;
  });

  // Filter out completely empty rows
  return mapped.filter(obj =>
    EXPECTED_CSV_COLS.some(h => String(obj[h] ?? '').replace(/\u00A0/g, '').trim())
  );
};

const generateShortId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let r = '';
  for (let i = 0; i < 8; i++) r += chars[Math.floor(Math.random() * chars.length)];
  return r;
};

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Bulkprocessing() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');
  const [branch, setBranch] = useState(null);
  const [Dealercode, setDealercode] = useState('');
  const [DSFApproved, setDSFapproved] = useState(0);
  const [parentIntermediary, setParentintermediary] = useState(null);
  const [approved, setApproved] = useState(0);

  // ── Product & cover ──
  const [products, setProducts] = useState([]);
  const [productCode, setProductcode] = useState('');
  const [productName, setProductname] = useState('');
  const [prdCode, setPrdcode] = useState('');
  const [commissionRate, setCommissionrate] = useState('');
  const [iraCode, setIracode] = useState('');
  const [riskClass, setRiskclass] = useState('');
  const [coverOptions, setCoveroptions] = useState([]);
  const [coverOptioncode, setCoveroptioncode] = useState('');
  const [coverOption, setCoveroption] = useState(null);
  const [insuredItems, setInsureditems] = useState([]);
  const [selectedItem, setSelecteditem] = useState('');
  const [coverPeriod, setCoverperiod] = useState('');
  const [bodyType, setBodytype] = useState('');
  const [coverType, setCovertype] = useState('');
  const [iraNumber, setIranumber] = useState('');
  const [makeName, setMakename] = useState('');
  const [modelName, setModelname] = useState('');

  // ── CSV upload & data ──
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [paymentInitiated, setPaymentInitiated] = useState(false);

  // ── Payment state ──
  const [payAmount, setPayamount] = useState(0);
  const [mpesaPhone, setMpesaphone] = useState('');
  const [payText, setPaytext] = useState('SEND REQUEST');
  const [mpesaClicked, setMpesaclicked] = useState(false);
  const [isCert, setIscert] = useState(0);
  const [certURL, setCertURL] = useState('');
  const [errorMessage, setErrormessage] = useState('');
  const [retryText, setRetrytext] = useState('Retry');

  // ── Proposal state (stored from buyCover) ──
  const [selectedClient, setSelectedClient] = useState(null);
  const [proposalOID, setProposalOID] = useState('');
  const [insuredOID, setInsuredOID] = useState('');
  const [insuredSchedule, setInsureschedule] = useState('');
  const [bpNumber, setBpnumber] = useState(null);
  const [policyNumber, setPolicynumber] = useState('');

  // ── Wizard step ── 0=config, 1=upload/process, 2=payment
  const [step, setStep] = useState(0);

  // ── Processing progress ──
  const [processProgress, setProcessProgress] = useState({ current: 0, total: 0, label: '' });

  // ── Payment modal ──
  const [showPayModal, setShowPayModal] = useState(false);

  // ══════════════════════════════════════
  //  DERIVED STATE (memoised)
  // ══════════════════════════════════════
  const totalGross = useMemo(() => {
    if (!uploadedData || uploadedData.length === 0) return 0;
    return uploadedData.reduce((acc, row) => {
      const v = parseFloat(row?.GrossPremium);
      return acc + (isNaN(v) ? 0 : v);
    }, 0);
  }, [uploadedData]);

  const processed = useMemo(() => {
    if (!uploadedData?.length) return false;
    return uploadedData.some(r => r?.GrossPremium !== undefined && String(r.GrossPremium).trim() !== '');
  }, [uploadedData]);

  const allProposalsCreated = useMemo(() =>
    uploadedData.length > 0 && uploadedData.every(r => r.proposalOID),
  [uploadedData]);

  // ══════════════════════════════════════
  //  AUTH HEADERS HELPER
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INSERT LOG (was missing)
  // ══════════════════════════════════════
  const insertLog = useCallback(async (code, activity) => {
    try {
      await Axios.post(`${API}/insertLog`, { agentCode: code, activity, today: getNow() }, { headers: authHeaders() });
    } catch (e) { console.error('insertLog error', e); }
  }, [authHeaders]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details (deduplicated — was duplicated in original)
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name);
        setBranch(data.Companyy);
        setDealercode(data.Dealer_type);
        setDSFapproved(data.DSFAllowed);
        setParentintermediary(data.ParentIntermediary);
        setApproved(data.Approved ? 1 : 0);
      } catch (e) { console.error('getDealerdetails error', e); }
    })();
  }, [agent, isAuth, authHeaders]);

  // Products
  useEffect(() => {
    if (!isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getProducts`, { method: 'GET', headers: authHeaders() });
        const data = await res.json();
        setProducts(data);
      } catch (e) { console.error('getProducts error', e); }
    })();
  }, [isAuth, authHeaders]);

  // Product details + cover options (combined, deduplicated)
  useEffect(() => {
    if (!productCode || !isAuth) return;
    (async () => {
      try {
        const [prodRes, coRes] = await Promise.all([
          fetch(`${API}/getThisproduct?OID=${productCode}`, { method: 'GET', headers: authHeaders() }),
          fetch(`${API}/getCoveroptions?product=${productCode}`, { method: 'GET', headers: authHeaders() }),
        ]);
        const prodData = await prodRes.json();
        setProductname(prodData.ProductDescription);
        setPrdcode((prodData.ProductCode || '').replace(/PRD-/g, ''));
        setCommissionrate(prodData.CommissionRate);
        setIracode(prodData.IRACode);
        setRiskclass(prodData.RiskClass);
        setCoveroptions(await coRes.json());
      } catch (e) { console.error('getProductdetails error', e); }
    })();
  }, [productCode, isAuth, authHeaders]);

  // Insured items + cover option details (deduplicated — was duplicated in original)
  useEffect(() => {
    if (!coverOptioncode || !isAuth) return;
    (async () => {
      try {
        const [itemsRes, detRes] = await Promise.all([
          fetch(`${API}/getInsureditems?coverOption=${coverOptioncode}`, { method: 'GET', headers: authHeaders() }),
          fetch(`${API}/getCoveroptiondetails?cover=${coverOptioncode}`, { method: 'GET', headers: authHeaders() }),
        ]);
        const items = await itemsRes.json();
        setInsureditems(items);
        if (items.length === 1) setSelecteditem(items[0].OID);
        const det = await detRes.json();
        setCoveroption(det.CoverOption);
      } catch (e) { console.error('getCoveroptiondetails error', e); }
    })();
  }, [coverOptioncode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  COVER PERIOD OPTIONS (product/coverOption-aware)
  // ══════════════════════════════════════
  const coverPeriodOptions = useMemo(() => {
    const pc = Number(productCode);
    const co = Number(coverOption);
    const isPsv = (pc === 4638 || pc === 3587) && co === 2;
    const isTuktuk = pc === 4695 || pc === 4694;
    const isTP = co === 2 && !isPsv && !isTuktuk;
    const isComp = co === 1 || co === 58;

    if (isPsv) return [
      { v: 'Week', l: '7 Days' }, { v: 'Fortnight', l: '14 Days' },
      { v: 'Monthly', l: '30 Days' }, { v: 'Annually', l: 'Annual' },
      { v: '3Installments', l: '3 Installments' }, { v: '6Installments', l: '6 Installments' },
      { v: '10Installments', l: '10 Installments' },
    ];
    if (isTuktuk) return [{ v: 'Monthly', l: '30 Days' }];
    if (isTP) return [{ v: 'Annually', l: 'Annual' }, { v: '2installments', l: '2 Installments' }];
    if (isComp) return [{ v: 'Monthly', l: '30 Days' }, { v: '3installments', l: '3 Installments' }];
    return [];
  }, [productCode, coverOption]);

  // ══════════════════════════════════════
  //  CSV HANDLING
  // ══════════════════════════════════════
  const downloadSampleCsv = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/samplebullkprocessing.csv`;
    link.setAttribute('download', 'samplebullkprocessing.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      e.target.value = null;
      return;
    }
    setSelectedFile(file);
  };

  /** Upload CSV: parse locally, enrich dates, upload to backend */
  const uploadFile = useCallback(async () => {
    if (!selectedFile) { toast.error('No file selected'); return false; }
    setUploading(true);
    try {
      // Parse client-side
      const text = await readFileAsText(selectedFile);
      const parsed = parseCsvText(text);
      if (!parsed.length) { toast.error('CSV has no valid data rows'); setUploading(false); return false; }

      // Enrich rows with CommencementUnix / ExpiryUnix
      const enriched = parsed.map(row => {
        const cd = parseDdMmYyyy(row.CommencementDate);
        if (!cd) return { ...row, CommencementUnix: '', ExpiryUnix: '' };
        const rowCover = row.CoverPeriod || row.coverPeriod || coverPeriod || '';
        return {
          ...row,
          CommencementUnix: formatDateTime(cd),
          ExpiryUnix: formatDateTime(computeExpiry(cd, rowCover)),
        };
      });
      setUploadedData(enriched);

      // Upload original file to backend
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await Axios.post(`${API}/uploadBulkCSV`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(credentials ? { Authorization: `Basic ${credentials}` } : {})
        }
      });
      toast.success(res.data?.message || `${enriched.length} rows loaded successfully`);
      setSelectedFile(null);
      setUploading(false);
      return true;
    } catch (err) {
      console.error('Upload error', err);
      const msg = err.response
        ? `${err.response.status} ${err.response.statusText}`
        : err.request ? 'No response from server' : err.message;
      toast.error(`Upload failed: ${msg}`);
      setUploading(false);
      return false;
    }
  }, [selectedFile, coverPeriod, credentials]);

  const clearUploadedData = () => {
    setUploadedData([]);
    setSelectedFile(null);
    setPayamount(0);
    setPaymentInitiated(false);
    setProcessProgress({ current: 0, total: 0, label: '' });
    toast.info('Data cleared');
  };

  // ══════════════════════════════════════
  //  PROCESS POLICIES (calculate premiums + fetch make/model codes)
  // ══════════════════════════════════════
  const processPolicies = useCallback(async () => {
    // Guard checks
    if (Dealercode === 170 && DSFApproved !== "1") {
      toast.error('You need an agent account to proceed'); return;
    }
    if (approved === 0) {
      toast.error('Your account is not approved. Kindly contact support!'); return;
    }
    if (!uploadedData?.length) { toast.error('No uploaded rows to process'); return; }
    if (!productCode || !coverOptioncode) {
      toast.error('Select product and cover option before processing'); return;
    }

    setUploading(true);
    setProcessProgress({ current: 0, total: uploadedData.length, label: 'Calculating premiums…' });
    const headers = authHeaders();

    try {
      const results = [];

      // Sequential processing to avoid rate-limiting & allow progressive UI
      for (let i = 0; i < uploadedData.length; i++) {
        const row = { ...uploadedData[i] };
        setProcessProgress({ current: i + 1, total: uploadedData.length, label: `Row ${i + 1} of ${uploadedData.length}` });

        const sumInsured = row.SumInsured || row.CC || row.Capacity || '';
        const capacity = row.Capacity || '';
        if (!sumInsured || !capacity) {
          results.push({ ...row, GrossPremium: 'Missing SumInsured/Capacity' });
          continue;
        }

        // Ensure dates
        if (!row.CommencementUnix) {
          const cd = parseDdMmYyyy(row.CommencementDate);
          if (cd) {
            row.CommencementUnix = formatDateTime(cd);
            row.ExpiryUnix = formatDateTime(computeExpiry(cd, coverPeriod));
          }
        }

        // Fetch make/model codes
        try {
          if (row.Make && !row.MakeCode) {
            const r = await Axios.get(`${API}/getMakecode?make=${encodeURIComponent(row.Make)}`, { headers });
            row.MakeCode = r.data?.OID || '';
          }
          if (row.Model && !row.ModelCode) {
            const r = await Axios.get(`${API}/getModelcode?model=${encodeURIComponent(row.Model)}`, { headers });
            row.ModelCode = r.data?.OID || '';
          }
        } catch { /* continue */ }

        // Calculate premium
        try {
          const res = await Axios.get(`${API}/calculatePremium`, {
            params: {
              insuredItem: selectedItem || coverOptioncode,
              coverOption: coverOptioncode,
              sumInsured, capacity,
              coverPeriod: coverPeriod || '',
              product: productCode
            },
            headers
          });
          const pre = res.data?.premium ?? res.data?.Premium ?? null;
          if (pre !== null) {
            const tl = Math.round(0.002 * pre);
            const ph = Math.round(0.0025 * pre);
            row.GrossPremium = pre + tl + ph + STAMP_DUTY;
          } else {
            row.GrossPremium = 'No premium';
          }
        } catch {
          row.GrossPremium = 'Error';
        }

        results.push(row);
        // Progressive UI update
        setUploadedData([...results, ...uploadedData.slice(i + 1)]);
      }

      setUploadedData(results);
      await insertLog(agentCode, `Bulk premiums calculated for ${productName}. ${results.length} rows.`);
      toast.success('Premium calculation complete');
    } catch (err) {
      console.error('processPolicies error', err);
      toast.error('Processing failed');
    } finally {
      setUploading(false);
      setProcessProgress({ current: 0, total: 0, label: '' });
    }
  }, [uploadedData, productCode, coverOptioncode, coverPeriod, selectedItem, approved, Dealercode, DSFApproved, agentCode, productName, authHeaders, insertLog]);

  // ══════════════════════════════════════
  //  BUY COVER (create clients + proposals + bulk policies)
  // ══════════════════════════════════════
  const buyCover = useCallback(async () => {
    if (!uploadedData?.length) { toast.error('No rows to create customers for'); return; }
    setUploading(true);
    setProcessProgress({ current: 0, total: uploadedData.length, label: 'Creating proposals…' });

    try {
      // Get bulk policy count
      const bp = await fetch(`${API}/getBulkpolicies`);
      const bulkPolicies = await bp.json();
      const bpCount = (bulkPolicies.length || 0) + 1;
      setBpnumber(bpCount);

      const rows = [...uploadedData];
      const results = [];
      let successfulBulkPolicies = 0;
      const todayFormatted = getNow();

      for (let i = 0; i < rows.length; i++) {
        const row = { ...rows[i] };
        setProcessProgress({ current: i + 1, total: rows.length, label: `Creating ${i + 1} of ${rows.length}…` });

        // 1) Create/find client
        try {
          const clientRes = await Axios.post(`${API}/createClient`, {
            firstName: row.CustomerFirstName || '',
            lastName: row.CustomerLastName || '',
            otherNames: '',
            phone: row.CustomerPhoneNumber || '',
            email: row.CustomerEmail || '',
            idNumber: row.CustomerIDNumber || '',
            pin: row.CustomerKRA || '',
            agent: agentCode || agent
          }, { headers: authHeaders() });

          const cd = clientRes.data || {};
          row.clientOID = cd.OID || '';
          row.clientStatus = cd.exists ? 'exists' : (cd.success ? 'created' : 'error');
          if (cd.OID) setSelectedClient(cd.OID);
        } catch {
          row.clientStatus = 'error';
          results.push(row);
          setUploadedData([...results, ...rows.slice(i + 1)]);
          continue;
        }

        if (!row.clientOID) { results.push(row); setUploadedData([...results, ...rows.slice(i + 1)]); continue; }

        // 2) Create proposal
        const pm = decomposePremium(row.GrossPremium);
        const commUnix = row.CommencementUnix || '';
        const expUnix = row.ExpiryUnix || '';
        const coverDays = computeCoverDays(commUnix, expUnix);

        try {
          const proposalRes = await fetch(`${API}/createProposal2`, {
            method: 'POST',
            headers: { 'Authorization': `Basic ${credentials}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              commissionRate: commissionRate || 0,
              agentCode: agentCode || agent, agentName: agentName || '', prdCode: prdCode || '',
              client: row.CustomerIDNumber || '', commencementUnix: commUnix, expiryUnix: expUnix,
              today: todayFormatted, coverDays, reg: row.Registration || '',
              productCode: productCode || '', payAmount: parseFloat(row.GrossPremium) || 0,
              premium: pm.base, basicPremium: pm.basicPremium, commission: pm.commission,
              stampduty: pm.stampduty, PHCF: pm.phcf, trainingLevy: pm.tl,
              netCommission: pm.netCommission, netPremium: pm.netPremium,
              selectedClient: row.clientOID, makeCode: row.MakeCode || '', modelCode: row.ModelCode || '',
              capacity: row.Capacity || 0, color: row.Color || '', tonnage: row.Tonnage || 0,
              yom: row.YOM || '', coverOptioncode: coverOptioncode || '',
              sumInsured: row.SumInsured || 0, chassis: row.ChassisNumber || '',
              engineNumber: row.EngineNumber || '', cc: row.CC || '',
              selectedItem: selectedItem || '', branch: branch || ''
            }),
            credentials: 'include'
          });
          const propData = await proposalRes.json();

          if (propData.success) {
            row.proposalOID = propData.proposalOID || '';
            row.insuredOID = propData.insuredOID || '';
            row.IOID = propData.IOID || '';
            row.policyNumber = propData.policyNumber || '';
            row.proposalStatus = 'created';
            setProposalOID(row.proposalOID);
            setInsuredOID(row.insuredOID);
            setInsureschedule(row.IOID);

            // 3) Insert bulk policy
            try {
              await fetch(`${API}/insertBulkpolicy`, {
                method: 'POST',
                headers: { 'Authorization': `Basic ${credentials}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  proposalOID: row.proposalOID, insuredOID: row.insuredOID, IOID: row.IOID,
                  bpNumber: bpCount, agentCode: agentCode || agent, today: todayFormatted
                }),
                credentials: 'include'
              });
              successfulBulkPolicies++;
            } catch (bpErr) { console.error('insertBulkpolicy error', bpErr); }
          } else {
            row.proposalStatus = 'error';
          }
        } catch (propErr) {
          console.error('createProposal2 error', propErr);
          row.proposalStatus = 'error';
        }

        results.push(row);
        setUploadedData([...results, ...rows.slice(i + 1)]);
      }

      setUploadedData(results);

      if (successfulBulkPolicies > 0) {
        const totalAmount = results.reduce((sum, r) => sum + (parseFloat(r.GrossPremium) || 0), 0);
        setPayamount(totalAmount);
        setPaymentInitiated(true);
        toast.success(`${successfulBulkPolicies} of ${results.length} proposals created. Proceed to payment.`);
        setStep(2);
        setTimeout(() => setShowPayModal(true), 300);
      } else {
        toast.warning('No bulk policies were successfully created');
      }
    } catch (err) {
      console.error('buyCover error', err);
      toast.error('Customer creation failed');
    } finally {
      setUploading(false);
      setProcessProgress({ current: 0, total: 0, label: '' });
    }
  }, [uploadedData, agentCode, agent, agentName, prdCode, commissionRate, productCode, coverOptioncode, selectedItem, branch, credentials, authHeaders]);

  // ══════════════════════════════════════
  //  CERTIFICATE HELPERS
  // ══════════════════════════════════════
  const insertCertificate = async (certLink, certNo, commencement, expiry, propOID, insOID) => {
    try {
      await fetch(`${API}/insertCertificate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ proposalOID: propOID, insuredOID: insOID, expiryUnix: expiry, commencementUnix: commencement, certNo, certLink, today: getNow() })
      });
    } catch (e) { console.error('insertCertificate error', e); }
  };

  const insertCertificateError = async (errorMes, propOID, insOID, expiryUnix, commencementUnix) => {
    try {
      await fetch(`${API}/insertCertificateerror`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ proposalOID: propOID, insuredOID: insOID, expiryUnix, commencementUnix, errorMes, today: getNow() })
      });
    } catch (e) { console.error('insertCertificateError error', e); }
  };

  // ══════════════════════════════════════
  //  PAYMENT → APPROVAL → CERTIFICATES
  // ══════════════════════════════════════
  const initiatePayment = useCallback(async () => {
    if (!mpesaPhone) { toast.error('Enter M-Pesa phone number'); return; }
    if (totalGross <= 0) { toast.error('No payable amount'); return; }

    setMpesaclicked(true);
    setPaytext('CHECKING PAYMENT...');
    toast.success('Prompt sent to phone!');

    const formattedNumber2 = '254' + mpesaPhone.replace(/^0/, '');
    const ref = generateShortId();
    const qref = `QP${ref}`;
    const dateDone = getNow();
    const accountNumber = `DA-BP00${bpNumber || 0}`;

    try {
      const res2 = await Axios.post(PAYMENT_WEBHOOK, { formattedNumber2, payAmount: 1, reg: accountNumber, qref, dateDone });
      const responseData = res2.data;

      if (responseData.success !== true) {
        toast.error('Payment failed. Please try again.');
        setPaytext('SEND REQUEST');
        setMpesaclicked(false);
        return;
      }

      const transactionId = responseData.transactionId || 'N/A';
      toast.success('Payment successful!');
      setPaytext('APPROVAL PROCESS ONGOING...');

      const todayFormatted = getNow();

      // Approve/initiate receipt
      const initiateres = await fetch(`${API}/approveInitiate`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${credentials}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentCode: agentCode || agent, payAmount: totalGross,
          selectedClient: agentCode, transaction: transactionId, today: todayFormatted,
        }),
        credentials: 'include'
      });
      const initiateData = await initiateres.json();
      const { docNo2, noteOID2 } = initiateData;

      // Approve each proposal + generate certificate
      const rows = [...uploadedData];
      let successfulApprovals = 0;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row.proposalOID) continue;

        setPaytext(`APPROVING ${i + 1} of ${rows.length}...`);

        const pm = decomposePremium(row.GrossPremium);
        const commUnix = row.CommencementUnix || '';
        const expUnix = row.ExpiryUnix || '';
        const coverDays = computeCoverDays(commUnix, expUnix);

        try {
          const approvalRes = await fetch(`${API}/createProposal3`, {
            method: 'POST',
            headers: { 'Authorization': `Basic ${credentials}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              utilize: 0, proposalOID: row.proposalOID,
              commissionRate: commissionRate || 0,
              agentCode: agentCode || agent, agentName: agentName || '', prdCode: prdCode || '',
              client: row.CustomerIDNumber || '', commencementUnix: commUnix, expiryUnix: expUnix,
              today: todayFormatted, coverDays, reg: row.Registration || '',
              productCode: productCode || '', payAmount: totalGross,
              premium: pm.base, basicPremium: pm.basicPremium, commission: pm.commission,
              stampduty: pm.stampduty, PHCF: pm.phcf, trainingLevy: pm.tl,
              netCommission: pm.netCommission, netPremium: pm.netPremium,
              selectedClient: row.clientOID || selectedClient,
              makeCode: row.MakeCode || '', modelCode: row.ModelCode || '',
              capacity: row.Capacity || 0, color: row.Color || '',
              tonnage: row.Tonnage || 0, yom: row.YOM || '',
              coverOptioncode: coverOptioncode || '', sumInsured: row.SumInsured || 0,
              chassis: row.ChassisNumber || '', engineNumber: row.EngineNumber || '',
              cc: row.CC || '', selectedItem: selectedItem || '',
              transaction: transactionId, DSFApproved: DSFApproved || '',
              parentIntermediary: parentIntermediary || '',
              docNo2: docNo2 || '', noteOID2: noteOID2 || '',
            }),
            credentials: 'include'
          });
          const approvalData = await approvalRes.json();

          if (approvalData.success) {
            successfulApprovals++;

            // Generate certificate
            try {
              setPaytext(`GENERATING CERTIFICATE ${i + 1} of ${rows.length}...`);
              const clientPhone = (row.CustomerPhoneNumber || '').replace(/^0/, '');
              const commDateShort = formatDateShort(commUnix);
              const expDateShort = formatDateShort(expUnix);

              const certRes = await Axios.post(CERT_WEBHOOK, {
                IOID: row.IOID || insuredSchedule,
                insuredOID: row.insuredOID || insuredOID,
                dateDone, selectedItem: selectedItem || '',
                proposalOID: row.proposalOID || proposalOID,
                coverType: coverType || '',
                clientName: `${row.CustomerFirstName || ''} ${row.CustomerLastName || ''}`.trim(),
                policyNumber: row.policyNumber || policyNumber,
                commencementUnix: commUnix, commencementDate: commDateShort,
                expiryUnix: expUnix, expiryDate: expDateShort,
                reg: row.Registration || '', chassis: row.ChassisNumber || '',
                clientPhone, bodyType: bodyType || '',
                capacity: row.Capacity || 0, makeName: row.Make || makeName || '',
                modelName: row.Model || modelName || '',
                engineNumber: row.EngineNumber || '',
                clientEmail: row.CustomerEmail || '',
                sumInsured: row.SumInsured || 0, clientPin: row.CustomerKRA || '',
                yom: row.YOM || '', iraCode: iraCode || '',
                tonnage: row.Tonnage || 0, iraNumber: iraNumber || '',
                DSFApproved: DSFApproved || '', parentIntermediary: parentIntermediary || '',
              });

              if (certRes.data.success) {
                await insertCertificate(certRes.data.certURL, certRes.data.certNo, commUnix, expUnix, row.proposalOID, row.insuredOID);
                setCertURL(certRes.data.certURL);
                row.certStatus = 'success';
              } else {
                await insertCertificateError(certRes.data.errorMessage, row.proposalOID, row.insuredOID, expUnix, commUnix);
                row.certStatus = 'failed';
              }
            } catch (certErr) {
              console.error(`Certificate error row ${i + 1}`, certErr);
              row.certStatus = 'failed';
            }
          }
        } catch (appErr) {
          console.error(`Approval error row ${i + 1}`, appErr);
        }
      }

      setUploadedData([...rows]);
      setPaytext('APPROVAL COMPLETE');
      toast.success(`${successfulApprovals} of ${rows.length} policies approved successfully!`);

      await insertLog(agentCode, `Bulk payment & approval: ${successfulApprovals} policies for ${productName}`);

      setTimeout(() => {
        setShowPayModal(false);
        setIscert(0);
        navigate('/active-policies');
      }, 2000);

    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment request failed. Please try again.');
      setPaytext('SEND REQUEST');
      setMpesaclicked(false);
    }
  }, [mpesaPhone, totalGross, bpNumber, uploadedData, agentCode, agent, agentName, prdCode, commissionRate, productCode, coverOptioncode, selectedItem, DSFApproved, parentIntermediary, credentials, selectedClient, insuredSchedule, insuredOID, proposalOID, policyNumber, coverType, bodyType, makeName, modelName, iraCode, iraNumber, authHeaders, insertLog, navigate, productName]);

  // retryCertficate (was missing — referenced in original render)
  const retryCertficate = useCallback(() => {
    setRetrytext('Retrying...');
    setIscert(0);
    setPaytext('SEND REQUEST');
    setRetrytext('Retry');
    toast.info('Please re-initiate the payment to retry certificate generation.');
  }, []);

  const closePayModal = () => {
    setIscert(0);
    setPaytext('SEND REQUEST');
    setShowPayModal(false);
  };

  // ── Config ready validation ──
  const configReady = productCode && coverOptioncode && coverPeriod;

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="bp-page">
      <Sidebar />
      <div className="bp-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="bp-top-row">
          <span className="bp-company">Definite Assurance</span>
          <span className="bp-breadcrumb">Dashboard / Bulk Processing</span>
        </div>

        {/* Step Indicator */}
        <div className="bp-steps">
          {STEPS.map((s, idx) => (
            <div
              key={s.key}
              className={`bp-step ${idx === step ? 'active' : ''} ${idx < step ? 'completed' : ''}`}
              onClick={() => { if (idx < step) setStep(idx); }}
              style={{ cursor: idx < step ? 'pointer' : 'default' }}
            >
              <span className="bp-step-number">{idx + 1}</span>
              {s.label}
            </div>
          ))}
        </div>

        {/* ─── STEP 0: CONFIGURE ─── */}
        {step === 0 && (
          <div className="bp-card">
            <h3>Configuration</h3>
            <div className="bp-config-grid">
              {/* Product selection */}
              <div className="bp-config-section">
                <h5>Product & Cover</h5>
                <div className="bp-form-group">
                  <label>Product</label>
                  <select value={productCode} onChange={(e) => { setProductcode(e.target.value); setCoveroptioncode(''); setCoverperiod(''); }}>
                    <option value="">— Select Product —</option>
                    {products.map(p => <option key={p.OID} value={p.OID}>{p.ProductDescription}</option>)}
                  </select>
                </div>
                <div className="bp-form-group">
                  <label>Cover Option</label>
                  <select value={coverOptioncode} onChange={(e) => setCoveroptioncode(e.target.value)}>
                    <option value="">— Select Cover Option —</option>
                    {coverOptions.map(c => <option key={c.OID} value={c.OID}>{c.OptionName}</option>)}
                  </select>
                </div>
                <div className="bp-form-group">
                  <label>Payment Interval</label>
                  <select value={coverPeriod} onChange={(e) => setCoverperiod(e.target.value)}>
                    <option value="">— Select Interval —</option>
                    {coverPeriodOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              </div>

              {/* Upload section */}
              <div className="bp-config-section">
                <h5>CSV Upload</h5>
                <div className="bp-upload-area">
                  <FaCloudUploadAlt size={32} color="#1a5c5a" />
                  <p>Select a CSV file with vehicle & customer data</p>
                  <div className="bp-upload-actions">
                    <button className="bp-btn bp-btn-outline bp-btn-sm" onClick={downloadSampleCsv}>
                      <FaDownload /> &nbsp;Sample CSV
                    </button>
                  </div>
                  <input type="file" accept=".csv" onChange={handleFileChange} style={{ marginTop: 12 }} />
                  {selectedFile && <div className="bp-file-info">📄 {selectedFile.name}</div>}
                </div>
              </div>
            </div>

            <div className="bp-actions" style={{ marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                className="bp-btn bp-btn-primary"
                disabled={!configReady || !selectedFile || uploading}
                onClick={async () => { const success = await uploadFile(); if (success) setStep(1); }}
              >
                {uploading ? 'Uploading…' : 'Upload & Continue'}
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 1: UPLOAD & PROCESS ─── */}
        {step === 1 && (
          <div className="bp-card">
            <h3>Uploaded Data — {uploadedData.length} rows</h3>

            {processProgress.total > 0 && (
              <div className="bp-progress">
                <div className="bp-progress-bar">
                  <div className="bp-progress-bar-fill" style={{ width: `${(processProgress.current / processProgress.total) * 100}%` }} />
                </div>
                <span className="bp-progress-text">{processProgress.label}</span>
              </div>
            )}

            {uploadedData.length > 0 ? (
              <>
                <div className="bp-table-wrap">
                  <table className="bp-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {DISPLAY_COLS.map(h => <th key={h}>{h}</th>)}
                        {allProposalsCreated && <th>Status</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          {DISPLAY_COLS.map(col => (
                            <td key={col}>
                              {col === 'GrossPremium' && typeof row[col] === 'number'
                                ? `KES ${formatter(row[col])}`
                                : row[col] ?? ''}
                            </td>
                          ))}
                          {allProposalsCreated && (
                            <td>
                              {row.proposalStatus === 'created'
                                ? <span className="bp-status-ok"><FaCheckCircle /> OK</span>
                                : <span className="bp-status-err"><FaTimesCircle /> Err</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bp-table-footer">
                  <span className="bp-row-count">{uploadedData.length} row(s)</span>
                  {totalGross > 0 && (
                    <div className="bp-total-box">
                      Total Payable: KES {formatter(totalGross)}
                    </div>
                  )}
                </div>

                <div className="bp-actions" style={{ marginTop: 16 }}>
                  <button className="bp-btn bp-btn-danger bp-btn-sm" onClick={clearUploadedData}>
                    <FaTimes /> Clear
                  </button>
                  {!processed ? (
                    <button className="bp-btn bp-btn-primary" onClick={processPolicies} disabled={uploading}>
                      {uploading ? 'Processing…' : 'Calculate Premiums'}
                    </button>
                  ) : !allProposalsCreated ? (
                    <button className="bp-btn bp-btn-success" onClick={buyCover} disabled={uploading}>
                      {uploading ? 'Creating…' : 'Buy Cover (Create Proposals)'}
                    </button>
                  ) : (
                    <button className="bp-btn bp-btn-primary" onClick={() => { setStep(2); setShowPayModal(true); }}>
                      Proceed to Payment
                    </button>
                  )}
                  <button className="bp-btn bp-btn-outline bp-btn-sm" onClick={() => setStep(0)}>
                    ← Back
                  </button>
                </div>
              </>
            ) : (
              <div className="bp-empty">
                <p>No data loaded. Go back to upload a CSV.</p>
                <button className="bp-btn bp-btn-outline" onClick={() => setStep(0)}>← Back</button>
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 2: PAYMENT (inline summary) ─── */}
        {step === 2 && !showPayModal && (
          <div className="bp-card" style={{ textAlign: 'center' }}>
            <h3>Payment & Certificates</h3>
            <div className="bp-pay-amount">
              <h4>KES {formatter(totalGross)}</h4>
              <p>Total bulk premium for {uploadedData.length} policies</p>
            </div>
            <button className="bp-btn bp-btn-success" onClick={() => setShowPayModal(true)}>
              Make Payment
            </button>
            <button className="bp-btn bp-btn-outline" onClick={() => setStep(1)} style={{ marginLeft: 10 }}>
              ← Back
            </button>
          </div>
        )}

        {/* ─── PAYMENT MODAL ─── */}
        {showPayModal && (
          <div className="bp-modal-overlay" onClick={() => !mpesaClicked && closePayModal()}>
            <div className="bp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bp-modal-header">
                <h3>Bulk Payment</h3>
                {!mpesaClicked && (
                  <button className="bp-modal-close" onClick={closePayModal}>
                    <FaTimes />
                  </button>
                )}
              </div>
              <div className="bp-modal-body">
                {isCert === 0 && (
                  <>
                    <div className="bp-pay-amount">
                      <h4>KES {formatter(totalGross)}</h4>
                      <p>{uploadedData.length} policies — Bulk Payment #{bpNumber || '—'}</p>
                    </div>

                    <div className="bp-form-group">
                      <label>M-Pesa Phone Number</label>
                      <input
                        type="text"
                        placeholder="07XXXXXXXX"
                        value={mpesaPhone}
                        onChange={(e) => setMpesaphone(e.target.value)}
                        disabled={mpesaClicked}
                      />
                    </div>

                    <div className="bp-actions" style={{ justifyContent: 'center', marginTop: 16 }}>
                      <button
                        className="bp-btn bp-btn-success"
                        onClick={initiatePayment}
                        disabled={mpesaClicked || !mpesaPhone}
                      >
                        {payText}
                      </button>
                    </div>

                    {!mpesaClicked && (
                      <p
                        style={{ textAlign: 'center', marginTop: 16, color: '#e74c3c', cursor: 'pointer', fontSize: '0.85rem' }}
                        onClick={closePayModal}
                      >
                        Cancel
                      </p>
                    )}
                  </>
                )}

                {isCert === 1 && (
                  <div className="bp-cert-success">
                    <p>✅ Certificate generated!</p>
                    <a
                      className="bp-btn bp-btn-success"
                      href={certURL}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-block', textDecoration: 'none', marginTop: 10 }}
                    >
                      DOWNLOAD CERTIFICATE
                    </a>
                    <p style={{ marginTop: 12, fontSize: '0.82rem', color: '#888' }}>
                      If certificate does not download, click the button again.
                    </p>
                  </div>
                )}

                {isCert === 2 && (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <p style={{ color: '#e74c3c', fontWeight: 600 }}>ERROR: {errorMessage}</p>
                    <button className="bp-btn bp-btn-success" onClick={retryCertficate}>
                      <FaRedo /> {retryText}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
